export function isAlphaNumeric(string: string): boolean {
  let code: number;
  let i: number;
  let len: number;

  for (i = 0, len = string.length; i < len; i += 1) {
    code = string.charCodeAt(i);
    if (
      !(code > 47 && code < 58) && // numeric (0-9)
      !(code > 64 && code < 91) && // upper alpha (A-Z)
      !(code > 96 && code < 123)
    ) {
      // lower alpha (a-z)
      return false;
    }
  }
  return true;
}

export function hashCode(string: string): number {
  let hash = 0;
  let i: number;
  const len: number = string.length;
  for (i = 0; i < len; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash); // eslint-disable-line no-bitwise
  }

  return Math.abs(hash);
}

export function convertToNumberRange(string: string, from: number, to: number): number {
  return from + (hashCode(string) % (to - from + 1));
}

/**
 * Capitalize first letter
 *
 * @example
 *   capitalizeFirstLetter('hello world') // 'Hello world'
 */
export function capitalizeFirstLetter(string: string): string {
  return string?.charAt(0).toUpperCase() + string?.slice(1);
}

/**
 * Lowercase first letter
 *
 * @example
 *   lowercaseFirstLetter('Hello World') // 'hello World'
 */
export function lowercaseFirstLetter(string: string): string {
  return string.charAt(0).toLowerCase() + string.slice(1);
}

/**
 * plural(1, "foo", "bar"); // "foo"
 * plural(2, "foo", "bar"); // "bar"
 */
export function plural(metric: number, singularForm: string, pluralForm: string): string {
  return metric === 1 ? singularForm : pluralForm;
}

export const isUrl = (value: string): boolean => {
  /* eslint-disable no-useless-escape */
  if (value.trim().split(/\s/).length > 1) {
    return false;
  }

  const URL_EXPRESSION =
    /https?:\/\/(?:www.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9].[^\s]{2,}|www.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9].[^\s]{2,}|https?:\/\/(?:www.|(?!www))[a-zA-Z0-9].[^\s]{2,}|www.[a-zA-Z0-9].[^\s]{2,}/g;
  /* eslint-enable no-useless-escape */
  const regex = new RegExp(URL_EXPRESSION);
  return regex.test(value.trim());
};

export function uniqueId(): string {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0; // eslint-disable-line no-bitwise
    const v = c === "x" ? r : (r & 0x3) | 0x8; // eslint-disable-line no-bitwise
    return v.toString(16);
  });
}

const commasRegexp = /(\d+)(\d{3})/;

export function addCommas(number: number): string {
  const s = String(number);
  const splitted = s.split(".");
  let x1 = splitted[0];
  const x2 = splitted.length > 1 ? `.${splitted[1]}` : "";
  while (commasRegexp.test(x1)) {
    x1 = x1.replace(commasRegexp, "$1,$2");
  }
  return x1 + x2;
}

const VALID_SPECIAL_PROTOCOLS = ["mailto:", "tel:", "callto:", "skype:", "fax:", "sms:"];

export function normalizeBrowserLink(
  url?: string
): { success: true; url: string } | { success: false; url: undefined } {
  if (!url) {
    return { success: false, url: undefined };
  }

  if (VALID_SPECIAL_PROTOCOLS.some(protocol => url.startsWith(protocol))) {
    return { success: true, url: url.replace(/\s/g, "") };
  }

  let parsedUrl: URL;
  try {
    parsedUrl = new URL(url);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
    return { success: false, url: undefined };
  }

  if (parsedUrl) {
    const { host, username, password, pathname, search, hash, protocol } = parsedUrl;
    const userAuth = password ? `${username}:${password}@` : "";

    return {
      success: true,
      url: `${protocol}//${userAuth || ""}${host}${pathname || ""}${search || ""}${hash || ""}`,
    };
  }

  return { success: false, url: undefined };
}

export const prettyJoin = (stringsArray: string[], lastSeparator = "and"): string => {
  return stringsArray.join(", ").replace(/, ([^,]*)$/, ` ${lastSeparator} $1`);
};
